import { getOKRAccessControlFromOKR } from 'components/RabbitOKRSidePanel/AccessControl/useOKRAccessControl';
import { showOKRDetails } from 'components/RabbitOKRSidePanel/okrPanelControlSlice';
import {
  performClone,
  performDelete,
  performRealign,
} from 'components/RabbitOKRSidePanel/okrPanelKebabMenuActionsSlice';
import { intersection } from 'lodash';
import queryString from 'query-string';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';

import {
  CloneWithoutPadding,
  KeyResultWithoutPadding,
  ObjectiveWithoutPadding,
  OpenSidePanelWithoutPadding,
  RealignWithoutPadding,
  Archive,
} from 'assets/assetsV1/iconComponents';

import { OKR_TYPES } from 'constants/ProjectConstants';
import {
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendClickedRealignedEvent,
  sendCreatedKREvent,
  sendCreatedObjectiveEvent,
  sendOpenedKRPanelEvent,
  sendOpenedObjectivePanelEvent,
  sendPageViewEvent,
} from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

export const getCascadedOrHierarchyViewRoute = ({ location, isHierarchyView }) => {
  const queryParams = queryString.parse(location?.search);
  queryParams.isHierarchyView = isHierarchyView;
  return `${location?.pathname}?${queryString.stringify(queryParams)}`;
};

export const postProcessPopup = (params) => {
  // check callback is for menu
  if (params.type !== 'columnMenu') {
    return;
  }
  let lastColumnHeaderName = '';
  const displayColumns = params?.api?.columnModel?.displayedColumns;

  if (Array.isArray(displayColumns)) {
    lastColumnHeaderName = displayColumns[displayColumns.length - 1]?.userProvidedColDef?.headerName;
  }

  const { ePopup } = params;
  const newTop = 40;
  ePopup.style.top = `${newTop}px`;

  let oldLeftStr = ePopup.style.left;
  oldLeftStr = oldLeftStr.substring(0, oldLeftStr.indexOf('px'));

  const oldLeft = parseInt(oldLeftStr);
  let newLeft = oldLeft - ePopup.clientWidth + 25;

  if (!lastColumnHeaderName) return;

  if (params?.column?.userProvidedColDef?.headerName === lastColumnHeaderName) {
    newLeft = oldLeft - 4;
  }

  ePopup.style.left = `${newLeft}px`;
};

export const generateKebabMenuOptionsRevamp = ({
  page,
  data,
  dispatch,
  queryClient,
  selectedTimelines,
  objectivesName,
  keyResultsName,
  OBJECTIVE_TYPE,
  IS_KR_CASCADING_ENABLED,
  location,
  history,
  source,
}) => {
  const okr = data;
  const { canDeleteOKR, canEditOKR, canCreateKeyResult, canCreateObjective, canCloneKeyResult, canCloneObjective } =
    getOKRAccessControlFromOKR({
      okr,
      IS_KR_CASCADING_ENABLED,
      OBJECTIVE_TYPE,
    });

  const timeBoundDetails = okr?.timeBoundDetails ?? selectedTimelines?.[0];

  const refetchHandler = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData]);
  };

  const openDetailsPanel = () => {
    const { type, uuid, unMasked } = okr ?? {};
    if (!unMasked) {
      return;
    }

    if (type === 'OBJECTIVE') {
      // navigate to objective sidepanel
      sendClickedOnCreateObjectiveEvent({
        eventProperties: { source: source || 'Kebab Menu', page: page || source },
      });
      sendOpenedObjectivePanelEvent({
        eventProperties: { source: source || 'kebab Menu', page: page || source, type: 'Edit Obj Panel' },
      });
    } else {
      sendClickedOnCreateKRevent({
        eventProperties: { source: source || 'Kebab Menu', page: page || source },
      });
      sendOpenedKRPanelEvent({
        eventProperties: { source: source || 'kebab Menu', page: page || source, type: 'Edit KR Panel' },
      });
    }

    sendPageViewEvent({
      eventProperties: {
        page: okr.type === 'OBJECTIVE' ? 'Add Objective Panel' : 'Add KR Panel',
        type: 'Panel',
        source: source || 'kebab Menu',
      },
    });

    dispatch(
      showOKRDetails({
        id: uuid,
        type,
        onDelete: refetchHandler,
        onUpdate: refetchHandler,
        history,
      })
    );
  };

  const openObjectiveCreationPanel = ({ okrPanelType = OKR_TYPES.OBJECTIVE }) => {
    sendClickedOnCreateObjectiveEvent({
      eventProperties: { source: source || 'Kebab Menu', page: page || source },
    });
    sendOpenedObjectivePanelEvent({
      eventProperties: { source: source || 'kebab Menu', page: page || source, type: 'Add Obj Panel' },
    });
    dispatch(
      openCreateOKRPanel({
        selectedObjective: {
          uuid: okr.uuid,
          text: okr.title,
          type: okr.type === OKR_TYPES.OBJECTIVE ? OKR_TYPES.OBJECTIVE : OKR_TYPES.KEY_RESULT,
          visibilityType: okr.visibilityType,
        },
        selectedTimeBoundPeriod: timeBoundDetails,
        okrPanelType,
        parentMeasure: okr.metric,
        parentMeasureType: okr.metricName,
        parentProgressType: okr.progressType,
        parentPost: okr?.parentPost,
        onKeyResultCreation(okrDetails) {
          sendCreatedKREvent({
            eventProperties: {
              source: source || 'Kebab Menu',
              page: page || source,
              goalId: okrDetails?.uuid,
            },
          });
        },
        onObjectiveCreation(okrDetails) {
          sendCreatedObjectiveEvent({
            eventProperties: {
              source: source || 'Kebab Menu',
              page: page || source,
              objectiveId: okrDetails?.uuid,
              description: okrDetails?.description,
              level: okrDetails?.objectiveLevel,
              type: okrDetails?.type,
              timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
            },
          });
        },
        history,
        location,
      })
    );
  };

  const openKRCreationPanel = () => {
    sendClickedOnCreateKRevent({
      eventProperties: { source: source || 'Kebab Menu', page: page || source },
    });
    sendOpenedKRPanelEvent({
      eventProperties: { source: source || 'kebab Menu', page: page || source, type: 'Add KR Panel' },
    });
    openObjectiveCreationPanel({ okrPanelType: OKR_TYPES.KEY_RESULT });
  };

  const openRealignPanel = () => {
    sendClickedRealignedEvent({
      eventProperties: { source: source || 'kebab Menu', page: page || source, type: 'Realign', goalId: okr.uuid },
    });
    openDetailsPanel();
    dispatch(performRealign());
  };

  const handleDelete = () => {
    sendClickedRealignedEvent({
      eventProperties: { source: 'kebab Menu', page: page || source, type: 'Delete', goalId: okr.uuid },
    });
    openDetailsPanel();
    dispatch(performDelete());
  };

  const cloneObjective = () => {
    openDetailsPanel();
    dispatch(performClone());
  };

  /* Access Control for OKR Menu Options starts here */

  let optionsToShow = ['OPEN_DETAILS', 'CREATE_OBJECTIVE', 'CREATE_KR', 'REALIGN', 'CLONE', 'DELETE'];

  const primaryActions = [optionsToShow[0]];
  const secondaryActions = [optionsToShow[1], optionsToShow[2]];
  const tertiaryActions = [optionsToShow[3], optionsToShow[4], optionsToShow[5]];

  const optionsLabelMap = {
    OPEN_DETAILS: `Open Details`,
    CREATE_OBJECTIVE: `Add child ${objectivesName}`,
    CREATE_KR: `Add child ${keyResultsName}`,
    REALIGN: 'Re-align',
    CLONE: 'Make a clone',
    DELETE: 'Archive',
  };

  const optionsOnClickMap = {
    OPEN_DETAILS: openDetailsPanel,
    CREATE_OBJECTIVE: openObjectiveCreationPanel,
    CREATE_KR: openKRCreationPanel,
    REALIGN: openRealignPanel,
    CLONE: cloneObjective,
    DELETE: handleDelete,
  };

  const optionsIconMap = {
    OPEN_DETAILS: OpenSidePanelWithoutPadding,
    CREATE_OBJECTIVE: ObjectiveWithoutPadding,
    CREATE_KR: KeyResultWithoutPadding,
    REALIGN: RealignWithoutPadding,
    CLONE: CloneWithoutPadding,
    DELETE: Archive,
  };

  if (!canDeleteOKR) {
    optionsToShow = optionsToShow.filter((item) => item !== 'DELETE');
  }
  if (!canEditOKR) {
    optionsToShow = optionsToShow.filter((item) => item !== 'REALIGN');
  }

  if (!canCreateKeyResult) {
    optionsToShow = optionsToShow.filter((item) => item !== 'CREATE_KR');
  }
  if (!canCreateObjective) {
    optionsToShow = optionsToShow.filter((item) => item !== 'CREATE_OBJECTIVE');
  }

  if (okr?.type === OKR_TYPES.OBJECTIVE) {
    optionsToShow = canCloneObjective ? optionsToShow : optionsToShow.filter((item) => item !== 'CLONE');
  } else {
    optionsToShow = canCloneKeyResult ? optionsToShow : optionsToShow.filter((item) => item !== 'CLONE');
  }

  const availablePrimaryActions = intersection(optionsToShow, primaryActions);
  const availableSecondaryActions = intersection(optionsToShow, secondaryActions);
  const availableTertiaryActions = intersection(optionsToShow, tertiaryActions);

  const isSecondaryActionPresent = availableSecondaryActions.length > 0;
  const isTertiaryActionPresent = availableTertiaryActions.length > 0;

  const mapToOptionObj = (option, hasBottomSeparator) => ({
    label: optionsLabelMap[option],
    onClick: optionsOnClickMap[option],
    Icon: optionsIconMap[option],
    hasBottomSeparator,
  });

  const options = [
    ...availablePrimaryActions.map((option, index) =>
      mapToOptionObj(
        option,
        index === availablePrimaryActions.length - 1 ? isSecondaryActionPresent || isTertiaryActionPresent : false
      )
    ),
    ...availableSecondaryActions.map((option, index) =>
      mapToOptionObj(option, index === availableSecondaryActions.length - 1 ? isTertiaryActionPresent : false)
    ),
    ...availableTertiaryActions.map((option, index) => mapToOptionObj(option, false)),
  ];
  return options;
};
